class SideMenuComponent extends HTMLElement {
  constructor() {
    super();
    this.menuToggle = null;
    this.menuClose = null;
    this.resultsClose = null;
    this.mobileMenu = null;
    this.overlay = null;
    this.previouslyFocused = null;
    this.closeTimer = null;
  }

  connectedCallback() {
    this.menuToggle = this.querySelector('[data-ref="sidebar-toggle"]');
    this.menuClose = this.querySelector('[data-ref="sidebar-close"]');
    this.resultsClose = this.querySelector('[data-ref="sidebar-results-close"]');
    this.mobileMenu = this.querySelector('[data-ref="sidebar-menu"]');
    this.overlay = this.querySelector('[data-ref="sidebar-overlay"]');

    this.menuToggle?.addEventListener('click', this.#handleMenuOpen);
    this.menuClose?.addEventListener('click', this.#handleMenuClose);
    this.resultsClose?.addEventListener('click', this.#handleMenuClose);
    this.overlay?.addEventListener('click', this.#handleOverlayClick);
    this.overlay?.addEventListener('cancel', this.#handleDialogCancel);
    document.addEventListener('keydown', this.#handleKeyDown);

    this.menuToggle?.setAttribute('aria-expanded', 'false');
    this.overlay?.setAttribute('aria-hidden', 'true');
  }

  disconnectedCallback() {
    this.menuToggle?.removeEventListener('click', this.#handleMenuOpen);
    this.menuClose?.removeEventListener('click', this.#handleMenuClose);
    this.resultsClose?.removeEventListener('click', this.#handleMenuClose);
    this.overlay?.removeEventListener('click', this.#handleOverlayClick);
    this.overlay?.removeEventListener('cancel', this.#handleDialogCancel);
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.clearTimeout(this.closeTimer);
  }

  #handleMenuOpen = () => {
    this.open();
  };

  #handleMenuClose = () => {
    this.close();
  };

  #handleOverlayClick = (event) => {
    if (event.target === this.overlay) this.close();
  };

  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #handleKeyDown = (event) => {
    if (!this.overlay?.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#getFocusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      this.mobileMenu?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #getFocusableElements() {
    if (!this.mobileMenu) return [];

    return Array.from(
      this.mobileMenu.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0);
  }

  open() {
    if (!this.overlay || !this.mobileMenu) return;

    window.clearTimeout(this.closeTimer);
    this.previouslyFocused = document.activeElement;
    this.overlay.setAttribute('aria-hidden', 'false');
    this.overlay.classList.remove('is-closing');
    this.overlay.classList.add('is-open');
    if (this.overlay instanceof HTMLDialogElement && !this.overlay.open) {
      this.overlay.showModal();
    }
    this.menuToggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');

    window.requestAnimationFrame(() => this.menuClose?.focus());
  }

  close() {
    if (!this.overlay?.classList.contains('is-open')) return;

    this.overlay.classList.add('is-closing');
    this.menuToggle?.setAttribute('aria-expanded', 'false');

    this.closeTimer = window.setTimeout(() => {
      this.overlay.classList.remove('is-open', 'is-closing');
      if (this.overlay instanceof HTMLDialogElement && this.overlay.open) {
        this.overlay.close();
      }
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');

      if (this.previouslyFocused instanceof HTMLElement) {
        this.previouslyFocused.focus();
      } else {
        this.menuToggle?.focus();
      }
    }, 230);
  }
}

if (!customElements.get('side-menu-component')) {
  customElements.define('side-menu-component', SideMenuComponent);
}
